// src/admin/pros/ProDrawer.jsx
import React, { useEffect } from "react";

const VERIFICATION_BADGES = {
  verified: { label: "Verified", bg: "#D1FAE5", color: "#059669" },
  pending: { label: "Pending", bg: "#FEF3C7", color: "#92400E" },
  rejected: { label: "Rejected", bg: "#FEE2E2", color: "#DC2626" },
};

const STATUS_BADGES = {
  active: { label: "Active", bg: "#D1FAE5", color: "#059669" },
  suspended: { label: "Suspended", bg: "#FEE2E2", color: "#DC2626" },
  pending: { label: "Pending", bg: "#FEF3C7", color: "#92400E" },
};

function initials(name) {
  return name.split(" ").map(n => n[0]).slice(0, 2).join("");
}

function avatarBg(name) {
  const palette = ["#EEF2FF", "#F0FDF4", "#FEF3C7", "#FEE2E2", "#E0F2FE", "#FCE7F3", "#F5F3FF"];
  return palette[name.charCodeAt(0) % palette.length];
}

function avatarColor(name) {
  const palette = ["#4338CA", "#16A34A", "#D97706", "#DC2626", "#0369A1", "#BE185D", "#7C3AED"];
  return palette[name.charCodeAt(0) % palette.length];
}

export default function ProDrawer({ pro, onClose, onUpdate, showToast }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (pro) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [pro, onClose]);

  if (!pro) return null;

  const handleApprove = () => {
    onUpdate({ ...pro, verification: "verified", status: "active" });
    showToast(`${pro.name} approved`, "success");
  };

  const handleSuspend = () => {
    const newStatus = pro.status === "suspended" ? "active" : "suspended";
    onUpdate({ ...pro, status: newStatus });
    showToast(`${pro.name} ${newStatus === "suspended" ? "suspended" : "activated"}`, "success");
  };

  const handleFeature = () => {
    onUpdate({ ...pro, featured: !pro.featured });
    showToast(`${pro.name} ${pro.featured ? "unfeatured" : "featured"}`, "success");
  };

  const getJobStatusIcon = (status) => {
    if (status === "completed") return "✓ Completed";
    if (status === "disputed") return "⚠ Disputed";
    return "○ In Progress";
  };

  const getJobStatusColor = (status) => {
    if (status === "completed") return "#94A3B8";
    if (status === "disputed") return "#D97706";
    return "#3B82F6";
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.35)",
          backdropFilter: "blur(2px)",
          zIndex: 199,
        }}
      />
      <div style={{
        position: "fixed",
        right: 0,
        top: 0,
        bottom: 0,
        width: 420,
        background: "#ffffff",
        boxShadow: "-4px 0 40px rgba(0,0,0,0.14)",
        zIndex: 200,
        transition: "right 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        right: 0,
        display: "flex",
        flexDirection: "column",
        height: "100vh"
      }}>
        {/* Header */}
        <div style={{
          flexShrink: 0,
          height: 60,
          background: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 24px"
        }}>
          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 18,
            fontWeight: 700,
            color: "#0F172A"
          }}>
            Pro Profile
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid #E2E8F0",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "24px"
        }}>
          {/* Identity block */}
          <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: avatarBg(pro.name),
              color: avatarColor(pro.name),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
              fontWeight: 700,
              flexShrink: 0
            }}>
              {initials(pro.name)}
            </div>
            <div>
              <div style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 19,
                fontWeight: 700,
                color: "#0F172A",
                marginBottom: 4
              }}>
                {pro.name}
              </div>
              <div style={{
                fontSize: 13,
                color: "#64748B",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                marginBottom: 8
              }}>
                {pro.category} · {pro.location.split(",")[0]}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span style={{
                  background: VERIFICATION_BADGES[pro.verification].bg,
                  color: VERIFICATION_BADGES[pro.verification].color,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "3px 9px",
                  borderRadius: 20,
                  whiteSpace: "nowrap"
                }}>
                  {VERIFICATION_BADGES[pro.verification].label}
                </span>
                <span style={{
                  background: STATUS_BADGES[pro.status].bg,
                  color: STATUS_BADGES[pro.status].color,
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "3px 9px",
                  borderRadius: 20,
                  whiteSpace: "nowrap"
                }}>
                  {STATUS_BADGES[pro.status].label}
                </span>
                {pro.featured && (
                  <span style={{
                    background: "#EEF2FF",
                    color: "#4338CA",
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "3px 9px",
                    borderRadius: 20,
                    whiteSpace: "nowrap"
                  }}>
                    ⭐ Featured
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 10,
            marginBottom: 20
          }}>
            {[
              { label: "Rating", value: pro.rating },
              { label: "Jobs", value: pro.jobsCompleted },
              { label: "Reviews", value: pro.reviews.length },
            ].map(stat => (
              <div key={stat.label} style={{
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                padding: 12,
                textAlign: "center"
              }}>
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#0F172A"
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "#94A3B8"
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Profile info */}
          <div style={{ marginBottom: 20 }}>
            {[
              { label: "Phone", value: pro.phone },
              { label: "Email", value: pro.email },
              { label: "Location", value: pro.location },
              { label: "Joined", value: pro.joined },
              { label: "Pro ID", value: pro.id, mono: true },
            ].map(info => (
              <div key={info.label} style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "9px 0",
                borderBottom: "1px solid #F8FAFC"
              }}>
                <span style={{
                  fontSize: 13,
                  color: "#94A3B8",
                  fontWeight: 500,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}>
                  {info.label}
                </span>
                <span style={{
                  fontSize: 13,
                  color: "#334155",
                  fontWeight: 500,
                  fontFamily: info.mono ? "monospace" : "'Plus Jakarta Sans', sans-serif"
                }}>
                  {info.value}
                </span>
              </div>
            ))}
          </div>

          {/* Earnings panel */}
          <div style={{
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: 10,
            padding: 16,
            marginBottom: 20
          }}>
            <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#059669",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 8
            }}>
              Total Earned
            </div>
            <div style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 28,
              fontWeight: 700,
              color: "#065F46",
              marginBottom: 12
            }}>
              KES {pro.earnings.toLocaleString()}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{
                flex: 1,
                background: "rgba(255,255,255,0.7)",
                borderRadius: 7,
                padding: "10px 12px"
              }}>
                <div style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#065F46"
                }}>
                  KES {pro.lastPayout.toLocaleString()}
                </div>
                <div style={{
                  fontSize: 11,
                  color: "#059669"
                }}>
                  Last Payout
                </div>
              </div>
              <div style={{
                flex: 1,
                background: "rgba(255,255,255,0.7)",
                borderRadius: 7,
                padding: "10px 12px"
              }}>
                <div style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: pro.pendingPayout > 0 ? "#D97706" : "#065F46"
                }}>
                  KES {pro.pendingPayout.toLocaleString()}
                </div>
                <div style={{
                  fontSize: 11,
                  color: "#059669"
                }}>
                  Pending
                </div>
              </div>
            </div>
          </div>

          {/* Job history */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: "#94A3B8",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 12
            }}>
              Recent Jobs
            </div>
            {pro.jobHistory.slice(0, 5).map((job, idx) => (
              <div key={idx} style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: idx < 4 ? "1px solid #F8FAFC" : "none"
              }}>
                <div>
                  <div style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#0F172A",
                    marginBottom: 3
                  }}>
                    {job.name}
                  </div>
                  <div style={{
                    fontSize: 11,
                    color: getJobStatusColor(job.status),
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}>
                    {getJobStatusIcon(job.status)}
                  </div>
                </div>
                <div style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: job.status === "completed" ? "#0F172A" : "#D97706"
                }}>
                  KES {job.amount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {/* Reviews */}
          {pro.reviews.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 11.5,
                fontWeight: 700,
                color: "#94A3B8",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 12
              }}>
                Reviews · ★ {pro.rating}
              </div>
              {pro.reviews.slice(0, 3).map((review, idx) => (
                <div key={idx} style={{
                  background: "#F8FAFC",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 8
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6
                  }}>
                    <span style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "#0F172A"
                    }}>
                      {review.user}
                    </span>
                    <div style={{ display: "flex", gap: 2 }}>
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill={i < review.rating ? "#F59E0B" : "#E2E8F0"}
                          stroke={i < review.rating ? "#F59E0B" : "#E2E8F0"}
                          strokeWidth="1"
                        >
                          <polygon points="12 17.27 18.18 21 16.54 13.97 22 9.24 14.81 8.63 12 2 9.19 8.63 2 9.24 7.46 13.97 5.82 21 12 17.27" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 12.5,
                    color: "#64748B",
                    lineHeight: 1.5
                  }}>
                    {review.text}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Verification */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: "#94A3B8",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 12
            }}>
              Verification
            </div>
            {[
              { label: "ID Verified", value: pro.idVerified, trueText: "Verified", falseText: "Not Verified" },
              { label: "Documents Submitted", value: pro.docsSubmitted, trueText: "Submitted", falseText: "Missing" },
              { label: "Approval Status", value: pro.approvalStatus, trueText: "Approved", falseText: "Pending", isStatus: true },
            ].map(item => (
              <div key={item.label} style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "9px 0",
                borderBottom: "1px solid #F8FAFC"
              }}>
                <span style={{
                  fontSize: 13,
                  color: "#64748B",
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}>
                  {item.label}
                </span>
                <span style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 12,
                  background: (item.isStatus ? item.value === "approved" : item.value) ? "#D1FAE5" : (item.value === "rejected" ? "#FEE2E2" : "#FEF3C7"),
                  color: (item.isStatus ? item.value === "approved" : item.value) ? "#059669" : (item.value === "rejected" ? "#DC2626" : "#D97706")
                }}>
                  {item.isStatus
                    ? (item.value === "approved" ? "Approved" : item.value === "rejected" ? "Rejected" : "Pending")
                    : item.value ? item.trueText : item.falseText}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer - Sticky */}
        <div style={{
          flexShrink: 0,
          borderTop: "1px solid #E2E8F0",
          padding: "16px 24px",
          background: "#fff"
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12
          }}>
            <button
              onClick={handleApprove}
              style={{
                padding: "10px 16px",
                background: "#0F172A",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: "#fff",
                cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
              ✓ Approve
            </button>
            <button
              onClick={handleSuspend}
              style={{
                padding: "10px 16px",
                background: "#fff",
                border: "1px solid #FCA5A5",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: "#DC2626",
                cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
              {pro.status === "suspended" ? "⊙ Unsuspend" : "⊘ Suspend"}
            </button>
            <button
              onClick={() => {
                window.open(`/pro/${pro.id}`, "_blank");
              }}
              style={{
                padding: "10px 16px",
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: "#64748B",
                cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
              ↗ Full profile
            </button>
            <button
              onClick={handleFeature}
              style={{
                padding: "10px 16px",
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: "#64748B",
                cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
              {pro.featured ? "★ Unfeature" : "☆ Feature"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

